import discountTemplate from "./discounts.template.html";

ItemDiscountsContentDirective.$inject = ['ItemDiscountsControllerService'];

/*******************************************************************************
 * Component: itemDiscountsContent
 *
 * This is the component to be used to manage the list of <b>ItemDiscount</b>
 * objects for a product item in the AddChangeItemDialog. This will utilize
 * the addChangeItemDialog's component controller for shared data management.
 *******************************************************************************/
/* @ngInject */ export function ItemDiscountsContentDirective(ItemDiscountsControllerService) {
  return {
    require: { dialogCtrl: '^^addChangeItemDialogContent' },
    template: discountTemplate,
    controller: ItemDiscountsControllerService.controller,
    controllerAs: '$idCtrl',
    bindToController: true
  };
}

/* @ngInject */ export function ItemDiscountsControllerServiceProvider() {
  this.controller = controller;
  this.$get = function () {
    return { controller: this.controller };
  };
}

/* @ngInject */ function controller(
  $scope,
  AddChangeItemDiscountDialog,
  Alert,
  CommonUIGridTemplates,
  DiscountTypes,
  SERVER,
  uiGridConstants
) {
  /*****************************************************************************
   * Properties
   *****************************************************************************/
  /** Controller Instance. */ var mThis = this;
  /** AddChangeItemDiscount dialog displayed. */ var mDiscountDialogActive = false;

  /*---------------------------------------------------*/
  /* Edit Column cell template for the discounts grid. */
  /*---------------------------------------------------*/
  var mEditDiscountCellTemplate =
    '<div class="ui-grid-cell-contents">' +
    '<span class="csm-grid-space-target glyphicon glyphicon-edit" style="cursor:pointer" ng-click="grid.appScope.$idCtrl.handleDiscount(row.entity)">' +
    '</span>' +
    '</div>';

  /*-------------------------------------------------------*/
  /* Grid to display items that are part of a kit. This is */
  /* used in the sub items tab when adding/changing a kit. */
  /*-------------------------------------------------------*/
  mThis.dgDiscounts = {
    enableColumnMenus: false,
    enableColumnResizing: true,
    enableHorizontalScrollbar: uiGridConstants.scrollbars.NEVER,
    enableVerticalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
    minRowsToShow: 5,
    maxRowsToShow: 7,
    onRegisterApi: function (gridApi) {
      mThis.dgDiscounts.api = gridApi;
    },
    data: [],
    columnDefs: [
      {
        name: 'discount',
        labelFunction: discountTypeLabel
      },
      {
        name: 'account',
        labelFunction: discountAccountLabelFunction,
        maxWidth: 400
      },
      {
        name: 'price',
        cellFilter: 'currency',
        maxWidth: 125
      },
      {
        field: 'edit',
        displayName: ' ',
        maxWidth: 10,
        enableSorting: false,
        cellTemplate: mEditDiscountCellTemplate
      },
      {
        field: 'delete',
        displayName: ' ',
        maxWidth: 10,
        enableSorting: false,
        cellTemplate: CommonUIGridTemplates.CELL_DELETE_ROW,
        appScope: $scope
      }
    ]
  };

  /*****************************************************************************
   * allDiscountsAdded */
  /**
   * Returns an indication that all discount types have been added to the list.
   *
   * @return  Boolean  True=all discount types added. False otherwise.
   *****************************************************************************/
  mThis.allDiscountsAdded = function () {
    /*------------------------------------*/
    /* For every available discount type, */
    /* check if it exists in the grid.    */
    /*------------------------------------*/
    return DiscountTypes.every(function (type) {
      return mThis.dgDiscounts.data
        .filter(function (d) {
          return d.type == type.value;
        })
        .shift();
    });
  };

  /***************************************************************************
   * discountAccountLabelFunction */
  /**
   * Label function to display the account name for the provided item discount.
   *
   * @param   String   Matching account name. Empty string if no match.
   ***************************************************************************/
  function discountAccountLabelFunction(discount) {
    var match = mThis.dialogCtrl.salesAccountList
      .filter(function (acct) {
        return acct.accountID == discount.accountID;
      })
      .shift();
    return match ? match.accountName : '';
  }

  /***************************************************************************
   * discountTypeLabel */
  /**
   * Returns a string representation of the discount type for the passed
   * <b>ItemDiscount</b> object.
   ***************************************************************************/
  function discountTypeLabel(discount) {
    var d = DiscountTypes.filter(function (dt) {
      return dt.value == discount.type;
    }).shift();

    return d.label || '';
  }

  /***************************************************************************
   * handleDiscount */
  /**
   * Event handling for adding/editing item discounts.
   *
   * @param  disc  Discount to modify. Null=create new discount.
   ***************************************************************************/
  mThis.handleDiscount = function (disc) {
    /*------------------------------*/
    /* Dialog already active, exit. */
    /*------------------------------*/
    if (mDiscountDialogActive) return;

    /*-----------------------------------------------*/
    /* Set properties that will be passed to dialog. */
    /*-----------------------------------------------*/
    var addMode = !disc ? true : false;
    var itemPrice = mThis.item.amount;

    /*-----------------------------------*/
    /* Exit if trying to add a discount  */
    /* when all discounts already added. */
    /*-----------------------------------*/
    if (addMode && mThis.allDiscountsAdded()) return;

    /*----------------------------------------------------*/
    /* Discounts of each type can only appear once in     */
    /* the list. Get the list of types currently being    */
    /* used so they can be excluded from the dialog list. */
    /*----------------------------------------------------*/
    var exclude = !addMode
      ? []
      : (mThis.dgDiscounts.data || []).map(function (d) {
          return d.type;
        });

    /*-----------------------------------------*/
    /* Call the dialog to add/modify discount. */
    /*-----------------------------------------*/
    mDiscountDialogActive = true;
    AddChangeItemDiscountDialog.show(
      disc,
      mThis.dialogCtrl.salesAccountList,
      itemPrice,
      addMode,
      exclude
    )
      .then(function (discount) {
        /*--------------------------------*/
        /* New discount, add to the grid. */
        /*--------------------------------*/
        if (addMode) {
          /*------------------------------------------------------------*/
          /* Search the list of current discounts for the corresponding */
          /* type. If found, replace, otherwise add the discount.       */
          /*------------------------------------------------------------*/
          var found = false;
          for (var i = 0; i < (mThis.item.itemDiscounts || []).length; i++) {
            if (mThis.item.itemDiscounts[i].type == discount.type) {
              mThis.item.itemDiscounts[i] = discount;
              found = true;
              break;
            }
          }

          if (!found) mThis.dgDiscounts.data.push(discount);
        } else {
          /*---------------------------------*/
          /* Modifying an existing discount, */
          /* replace it the current in grid. */
          /*---------------------------------*/
          var curRowIndex = mThis.dgDiscounts.data.indexOf(disc);
          mThis.dgDiscounts.data[curRowIndex] = discount;
        }
      })
      ['finally'](function () {
        mDiscountDialogActive = false;
      });
  };

  /***************************************************************************
   * onInit */
  /**
   * Component initialization callback function.
   ***************************************************************************/
  mThis.$onInit = function () {
    /*-------------------------------------------------------------------*/
    /* Set up the onSelect tab handler to refresh the grid. A bug in     */
    /* ui-grid causes column headers to disappear when the viewport size */
    /* changes or when a grid hidden in the DOM is eventually shown.     */
    /*-------------------------------------------------------------------*/
    mThis.dialogCtrl.tabDiscounts.onSelect = function () {
      mThis.dgDiscounts.api.core.refresh();
    };

    /*------------------------------------------*/
    /* Set initialized to true so grid can load */
    /*------------------------------------------*/
    mThis.initialized = true;
  };

  /***************************************************************************
   * removeDiscount */
  /**
   * Click handler for the remove button in the discounts tab. Removes the
   * selected discount from the grid.
   *
   * @param  discountToRemove   Discount to be removed from the grid.
   ***************************************************************************/
  function removeDiscount(discountToRemove) {
    /*---------------------------*/
    /* Nothing to remove, abort. */
    /*---------------------------*/
    if (!discountToRemove) return;

    /*--------------------------------*/
    /* Remove discount from the grid. */
    /*--------------------------------*/
    Alert.confirm(
      'Are you sure you want to remove this discount?',
      'Confirm Remove'
    ).then(function () {
      var index = mThis.dgDiscounts.data.indexOf(discountToRemove);
      mThis.dgDiscounts.data.splice(index, 1);
    });
  }

  /*---------------------------------------------------*/
  /* Set up an accessor the item property from the     */
  /* dialogCtrl. Use an empty object for data binding. */
  /*---------------------------------------------------*/
  Object.defineProperty(mThis, 'item', {
    get: function () {
      return mThis.dialogCtrl ? mThis.dialogCtrl.item : {};
    }
  });

  /*--------------------------------------------*/
  /* Set up listener for the row delete event   */
  /*--------------------------------------------*/
  $scope.$on(
    CommonUIGridTemplates.EVENT_DELETE_ROW,
    function (event, grid, row) {
      removeDiscount(row.entity);
    }
  );

  /*-----------------------------------------------*/
  /* Set up a watcher for the item reference.      */
  /* When changed, set the dgDiscounts data source */
  /*-----------------------------------------------*/
  $scope.$watch(
    function () {
      return mThis.item;
    },
    function (item) {
      if (!item) return;

      /*----------------------*/
      /* Ensure a data source */
      /*----------------------*/
      if (!item.itemDiscounts) item.itemDiscounts = [];

      mThis.dgDiscounts.data = item.itemDiscounts;
    }
  );
}
